/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useEffect, useState } from 'react';
import { getStoredCustomerDetails } from '../../../Utilities/utils';
import JobViewApiService from '../../../../Modules/Jobs/Services/JobViewService';
import { DEFAULT_FROM_DATE, FiltersOfAllJobs, ITEMS_PER_PAGE, JobType } from '../../../Constants/App';
import { IJobProps } from '../Conversations.model';

export const useJobsList = (shouldFetch: boolean) => {
  const [jobs, setJobs] = useState<IJobProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCounts, setTotalCounts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchString, setSearchString] = useState('');

  const fetchJobs = useCallback(async (page: number, search: string) => {
    try {
      setIsLoading(true);
      const { customerId = '', userId } = getStoredCustomerDetails() || {};

      const allJobResponse = await JobViewApiService.jobView(
        customerId,
        userId,
        DEFAULT_FROM_DATE,
        JobType.Recurring,
        page,
        ITEMS_PER_PAGE,
        undefined,
        undefined,
        search ? [{ id: FiltersOfAllJobs.JobName, value: search }] : []
      );

      const rawJobs: IJobProps[] = allJobResponse?.data?.data ?? [];
      const total: number = allJobResponse?.data?.metadata?.totalCount ?? 0;

      setJobs(
        rawJobs?.map((job) => ({
          jobId: job?.jobId,
          jobName: job?.jobName,
          status: job?.status,
        }))
      );
      setTotalCounts(total);
    } catch (e) {
      console.error('Error fetching jobs:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      fetchJobs(currentPage, searchString);
    } else {
      setJobs([]);
      setTotalCounts(0);
      setCurrentPage(1);
      setSearchString('');
    }
  }, [shouldFetch, currentPage, searchString]);

  const handlePaginationChanges = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setCurrentPage(1);
    setSearchString(value);
  };

  return {
    jobs,
    isLoading,
    totalCounts,
    currentPage,
    searchString,
    setSearchString,
    handlePaginationChanges,
    handleSearch,
  };
};
